import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { isBuiltinScene } from '@numslearn/shared';
import { api, ApiError, type AssetRow, type MediaList } from './net/api.js';
import { builtinSceneUrl } from './game/PuzzlePicture.js';
import {
  LocalPictureError, addLocalPicture, isLocalPicture, listLocalPictures, localPicturesAvailable,
  removeLocalPicture,
} from './localPictures.js';

/**
 * Pictures and recordings, for the whole app.
 *
 * One store at the root rather than one per panel: the board draws its
 * "surprise me" picture from the same list the teacher is uploading into, so
 * a picture added in the panel is in the draw for the very next board — and a
 * deleted one is out of it — without anything having to be told.
 *
 * Two homes for a picture. A signed-in teacher's go to the server, where they
 * follow the teacher to any device and reach the children's tablets in class.
 * Without an account they go into this browser (see `localPictures`), which
 * is enough to survive a refresh and to practise with.
 */

/** One picture the board can hide, wherever it is kept. */
export interface PictureItem {
  id: string;
  name: string;
  bytes: number;
  src: string;
  /** Kept in this browser only — not available to a live class. */
  local: boolean;
}

export interface MediaStore {
  /** The signed-in teacher's server files, both kinds. */
  assets: AssetRow[];
  usedBytes: number;
  quotaBytes: number;
  /**
   * True once the server has confirmed a signed-in teacher.
   *
   * Recordings, the storage meter and the curriculum all need an account, and
   * a shelf reading "0 B of 0 B" with a dead Upload button is worse than none.
   */
  available: boolean;
  /** Every picture that can go under a board: the server's, then this browser's. */
  pictures: PictureItem[];
  /** Pictures can be added: signed in, or a browser that can keep them. */
  canUploadPictures: boolean;
  /** False until the first listing has come back, from both homes. */
  loaded: boolean;
  busy: boolean;
  error: string | null;
  /** Resolves with the new item's id, or null if the upload was refused. */
  upload: (kind: 'picture' | 'voice', file: File) => Promise<string | null>;
  remove: (id: string) => Promise<void>;
  refresh: () => Promise<void>;
  /** Where to load a picture from: a built-in scene, a server id or a local one. */
  pictureSrc: (id: string) => string | null;
}

const MediaCtx = createContext<MediaStore | null>(null);

interface LocalRow { id: string; name: string; bytes: number; src: string }

export function MediaProvider({ children }: { children: ReactNode }) {
  const [list, setList] = useState<MediaList>({ assets: [], usedBytes: 0, quotaBytes: 0 });
  const [available, setAvailable] = useState(false);
  const [local, setLocal] = useState<LocalRow[]>([]);
  const [localOk, setLocalOk] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Object URLs for local blobs, revoked when their picture goes. */
  const urls = useRef(new Map<string, string>());

  const refreshServer = useCallback(async () => {
    try {
      setList(await api.get<MediaList>('/assets'));
      setAvailable(true);
    } catch {
      // Not signed in, or offline: an empty shelf is the honest answer.
      setList({ assets: [], usedBytes: 0, quotaBytes: 0 });
      setAvailable(false);
    }
  }, []);

  const refreshLocal = useCallback(async () => {
    if (!(await localPicturesAvailable())) { setLocalOk(false); setLocal([]); return; }
    setLocalOk(true);
    const rows = await listLocalPictures();
    const keep = new Set(rows.map((r) => r.id));
    for (const [id, url] of urls.current) {
      if (!keep.has(id)) { URL.revokeObjectURL(url); urls.current.delete(id); }
    }
    setLocal(rows.map((r) => {
      let src = urls.current.get(r.id);
      if (!src) { src = URL.createObjectURL(r.blob); urls.current.set(r.id, src); }
      return { id: r.id, name: r.name, bytes: r.bytes, src };
    }));
  }, []);

  const refresh = useCallback(async () => {
    await Promise.all([refreshServer(), refreshLocal().catch(() => setLocalOk(false))]);
    setLoaded(true);
  }, [refreshLocal, refreshServer]);

  useEffect(() => { void refresh(); }, [refresh]);
  useEffect(() => () => {
    for (const url of urls.current.values()) URL.revokeObjectURL(url);
  }, []);

  const upload = useCallback(async (kind: 'picture' | 'voice', file: File) => {
    setBusy(true);
    setError(null);
    try {
      if (kind === 'picture' && !available) {
        const row = await addLocalPicture(file, mimeOf(file));
        await refreshLocal();
        return row.id;
      }
      const row = await api.upload<AssetRow>(
        `/assets/${kind}?name=${encodeURIComponent(file.name)}`, file, mimeOf(file),
      );
      await refreshServer();
      return row.id;
    } catch (e) {
      // The store's own code — too_large, quota_full, bad_type — so the
      // message can say which limit was hit rather than "upload failed".
      setError(e instanceof ApiError || e instanceof LocalPictureError ? e.code : 'generic');
      return null;
    } finally {
      setBusy(false);
    }
  }, [available, refreshLocal, refreshServer]);

  const remove = useCallback(async (id: string) => {
    setError(null);
    try {
      if (isLocalPicture(id)) {
        await removeLocalPicture(id);
        await refreshLocal();
      } else {
        await api.del(`/assets/${id}`);
        await refreshServer();
      }
    } catch {
      setError('generic');
    }
  }, [refreshLocal, refreshServer]);

  const pictures = useMemo<PictureItem[]>(() => [
    ...list.assets.filter((a) => a.kind === 'picture').map((a) => ({
      id: a.id, name: a.name, bytes: a.bytes, src: serverSrc(a.id), local: false,
    })),
    ...local.map((r) => ({ ...r, local: true })),
  ], [list.assets, local]);

  const pictureSrc = useCallback((id: string): string | null => {
    if (!id) return null;
    if (isBuiltinScene(id)) return builtinSceneUrl(id);
    if (isLocalPicture(id)) return urls.current.get(id) ?? null;
    return serverSrc(id);
  }, []);

  const value: MediaStore = {
    ...list, available, pictures,
    canUploadPictures: available || localOk,
    loaded, busy, error, upload, remove, refresh, pictureSrc,
  };
  return <MediaCtx.Provider value={value}>{children}</MediaCtx.Provider>;
}

export function useMedia(): MediaStore {
  const ctx = useContext(MediaCtx);
  if (!ctx) throw new Error('useMedia must be used inside <MediaProvider>');
  return ctx;
}

const serverSrc = (id: string): string => `/api/assets/${encodeURIComponent(id)}/raw`;

/**
 * The type to declare for a file.
 *
 * `File.type` is empty often enough to matter — some Android pickers and some
 * network shares hand over a photo with no type at all — and an empty
 * Content-Type is rejected by the server as `bad_type`, which reads to a
 * teacher as "my photo is not allowed" when it is perfectly fine. Fall back to
 * the extension, and let the store have the last word either way.
 */
const BY_EXT: Record<string, string> = {
  png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', webp: 'image/webp', gif: 'image/gif',
  mp3: 'audio/mpeg', ogg: 'audio/ogg', oga: 'audio/ogg', wav: 'audio/wav',
  webm: 'audio/webm', m4a: 'audio/mp4', aac: 'audio/aac',
};

function mimeOf(file: File): string {
  if (file.type) return file.type;
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  return BY_EXT[ext] ?? 'application/octet-stream';
}

/** "2.4 MB of 25 MB used" reads better than a byte count. */
export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
